import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home, LogIn, RefreshCw } from 'lucide-react';

const RSS_LOGO = "https://customer-assets.emergentagent.com/job_199c36c2-e07b-4567-84a2-e99d8a5503d6/artifacts/c9vy7sfe_rumpun_silat_sarawak_logo_transparent_1024.png";

/**
 * SuccessState — premium success screen after form submission
 * @param {string} title - main title
 * @param {string} message - main message
 * @param {object[]} nextSteps - array of {icon, label, desc} for next-step timeline
 * @param {object[]} actions - buttons [{label, to, variant, onClick, testId}]
 * @param {React.ReactNode} extra - optional additional content
 */
const SuccessState = ({
  title = 'Pendaftaran Berjaya Dihantar',
  message = 'Maklumat anda telah diterima dan akan melalui proses semakan sebelum disahkan.',
  nextSteps = [],
  actions = [],
}) => {
  return (
    <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center py-12 px-4" data-testid="success-state">
      <div className="w-full max-w-xl">
        {/* Card */}
        <div className="bg-white border border-[#E5E5E5] rounded shadow-sm overflow-hidden">
          {/* Top accent */}
          <div className="h-1 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920]" />

          <div className="p-8 sm:p-10">
            {/* Icon + Logo */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative mb-4">
                <div className="w-16 h-16 bg-[#F0FDF4] border-2 border-[#16a34a] rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-[#16a34a]" />
                </div>
                <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center border border-[#E5E5E5]">
                  <img src={RSS_LOGO} alt="RSS" className="w-5 h-5 object-contain" />
                </div>
              </div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#0B0B0B] text-center mb-2" data-testid="success-title">
                {title}
              </h1>
              <p className="text-sm text-[#555] text-center max-w-sm leading-relaxed">{message}</p>
            </div>

            {/* Next steps */}
            {nextSteps.length > 0 && (
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-widest text-[#D71920] mb-4">
                  Proses Seterusnya
                </p>
                <div className="space-y-3">
                  {nextSteps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#F7F7F7] rounded border border-[#E5E5E5]">
                      <div className="w-6 h-6 bg-[#0B0B0B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[10px] text-white font-bold">{i + 1}</span>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#0B0B0B]">{step.label}</p>
                        {step.desc && <p className="text-xs text-[#777] mt-0.5">{step.desc}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notice */}
            <div className="mb-8 p-4 bg-[#FFFBF0] border border-[#D4AF37]/30 rounded text-xs text-[#7a6200] leading-relaxed">
              Pihak sekretariat akan menyemak maklumat anda sebelum pengesahan dibuat.
              Sila pastikan emel yang diberikan adalah aktif untuk menerima notifikasi.
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {actions.map((action, i) => {
                const base = "inline-flex items-center justify-center gap-2 text-sm font-semibold px-5 py-2.5 rounded transition-colors";
                const isPrimary = i === 0;
                const cls = isPrimary
                  ? `${base} bg-[#FFD21F] hover:bg-[#f5c800] text-[#0B0B0B] flex-1`
                  : `${base} border border-[#E5E5E5] hover:border-[#0B0B0B] text-[#555] hover:text-[#0B0B0B] flex-1`;

                if (action.to) {
                  return (
                    <Link key={i} to={action.to} className={cls} data-testid={action.testId}>
                      {action.label} {isPrimary && <ArrowRight size={14} />}
                    </Link>
                  );
                }
                return (
                  <button key={i} onClick={action.onClick} className={cls} data-testid={action.testId}>
                    {action.icon && <action.icon size={14} />}
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-[#bbb] mt-4">
          Rumpun Silat Sarawak · <span className="italic">Berseni Berbudi Berbakti</span>
        </p>
      </div>
    </div>
  );
};

export default SuccessState;
